<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software: you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation, either
 * version 3 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with OrangeHRM.
 * If not, see <https://www.gnu.org/licenses/>.
 */
 -->

<template>
  <oxd-dialog @update:show="onCancel">
    <div class="orangehrm-modal-header">
      <oxd-text type="card-title">
        Edit Payment ( {{ customerName }} ) {{ invoiceNumber }}
      </oxd-text>
    </div>
    <oxd-divider />
    <oxd-form :loading="isLoading" @submit-valid="onSave">
      <oxd-form-row>
        <oxd-grid :cols="2" class="orangehrm-full-width-grid">
          <oxd-grid-item>
            <date-input
              v-model="invoicepayment.date"
              :label="$t('general.date')"
              :rules="rules.date"
              :years="yearsArray"
              required
            />
          </oxd-grid-item>
          <oxd-grid-item>
            <oxd-input-field
              v-model="invoicepayment.amount"
              :label="$t('general.amount')"
              :rules="rules.amount"
              required
            />
          </oxd-grid-item>
        </oxd-grid>
      </oxd-form-row>

      <oxd-form-row>
        <oxd-grid :cols="1" class="orangehrm-full-width-grid">
          <oxd-grid-item>
            <oxd-input-field
              v-model="invoicepayment.note"
              type="textarea"
              :label="$t('general.description')"
              :rules="rules.note"
              required
            />
          </oxd-grid-item>
        </oxd-grid>
      </oxd-form-row>

      <oxd-divider />
      <oxd-form-actions>
        <required-text />
        <oxd-button
          type="button"
          display-type="ghost"
          :label="$t('general.cancel')"
          @click="onCancel"
        />
        <submit-button />
      </oxd-form-actions>
    </oxd-form>
  </oxd-dialog>
</template>

<script>
import {APIService} from '@ohrm/core/util/services/api.service';
import {required, validDateFormat} from '@/core/util/validation/rules';
import useDateFormat from '@/core/util/composable/useDateFormat';
import {OxdDialog} from '@ohrm/oxd';
import {toRefs} from 'vue';
import {
  shouldNotExceedCharLength,
  digitsOnlyWithDecimalPoint,
  maxCurrency,
  digitsOnlyWithTwoDecimalPoints,
} from '@ohrm/core/util/validation/rules';

const invoicepaymentModel = {
  date: null,
  amount: null,
  note: null,
  customerId: null,
  invoiceNumber: null,
};

export default {
  name: 'Edit',

  components: {
    'oxd-dialog': OxdDialog,
  },

  props: {
    data: {
      type: Object,
      required: true,
    },
    custName: {
      type: String,
      required: true,
    },
    invTotal: {
      type: String,
      required: true,
    },
    invNumber: {
      type: String,
      required: true,
    },
    custId: {
      type: String,
      required: true,
    },
  },

  emits: ['close'],

  setup(props) {
    const http = new APIService(
      window.appGlobal.baseUrl,
      `/api/v2/pim/invoicepayments`,
    );
    const {userDateFormat} = useDateFormat();

    const {custName: customerName} = toRefs(props);
    const {custId: customerId} = toRefs(props);
    const {invNumber: invoiceNumber} = toRefs(props);
    const {invTotal: invoiceTotal} = toRefs(props);

    return {
      http,
      userDateFormat,
      customerName,
      customerId,
      invoiceNumber,
      invoiceTotal,
    };
  },

  data() {
    return {
      isLoading: false,
      selectedOption: {},
      invoicepayment: {
        ...invoicepaymentModel,
      },
      rules: {
        type: [required],
        date: [required, validDateFormat(this.userDateFormat)],
        note: [shouldNotExceedCharLength(1000)],
        amount: [
          required,
          digitsOnlyWithDecimalPoint,
          maxCurrency(10000000000),
          digitsOnlyWithTwoDecimalPoints,
        ],
      },
    };
  },

  beforeMount() {
    this.isLoading = true;
    this.http
      .get(this.data.id)
      .then((response) => {
        const {data} = response.data;
        this.invoicepayment = data;
        this.invoicepayment.date = data.date.date.split(' ')[0];
        this.invoicepayment.amount = parseFloat(data.amount).toFixed(2);
      })
      .finally(() => {
        this.isLoading = false;
      });
  },

  methods: {
    onSave() {
      this.isLoading = true;
      //console.log(this.invoicepayment);
      this.http
        .update(this.data.id, {
          invoiceDate: this.invoicepayment.date,
          datePaid: this.invoicepayment.date,
          amount: Number(this.invoicepayment.amount).toFixed(2),
          note: this.invoicepayment.note,
          invoiceNumber: this.invoiceNumber,
          customerId: this.customerId,
        })
        .then(() => {
          return this.$toast.updateSuccess();
        })
        .then(() => {
          this.invoicepayment.customerId = this.customerId;
          this.invoicepayment.invoiceNumber = this.invoiceNumber;
          this.invoicepayment = {...invoicepaymentModel};
          this.onCancel();
        });
    },
    onCancel() {
      this.$emit('close', true);
    },
  },
};
</script>
